/**
 * Cart upsell — scale offer headline to max 2 lines within one line-height slot
 * at the default max font-size. Uses an off-DOM clone for measurement so flex /
 * Alpine x-show do not corrupt scrollHeight or line counts.
 */
(function () {
  const SEL_WRAP = '[data-cart-upsell-label-fit]';
  const SEL_LABEL = '.cart-upsell-offer__label';

  const roMap = new WeakMap();
  let rafId = 0;

  function parseCssPx(value, fallback) {
    if (!value || !String(value).trim()) return fallback;
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : fallback;
  }

  function countLines(el) {
    const r = el.getClientRects();
    if (!r.length) return 1;
    let n = 1;
    let y = r[0].top;
    for (let i = 1; i < r.length; i++) {
      if (r[i].top > y + 1) {
        n++;
        y = r[i].top;
      }
    }
    return n;
  }

  /**
   * Lay out a clone at a fixed width (the live text column) so line wraps match the drawer.
   */
  function measureClone(labelEl, widthPx, fs) {
    const clone = labelEl.cloneNode(true);
    const cs = getComputedStyle(labelEl);
    Object.assign(clone.style, {
      position: 'fixed',
      left: '-10000px',
      top: '0',
      width: widthPx + 'px',
      boxSizing: 'border-box',
      margin: '0',
      padding: '0',
      visibility: 'hidden',
      pointerEvents: 'none',
      whiteSpace: 'normal',
      overflowWrap: 'break-word',
      wordBreak: 'break-word',
      fontSize: fs + 'px',
      lineHeight: fs + 'px',
    });
    clone.style.fontFamily = cs.fontFamily;
    clone.style.fontWeight = cs.fontWeight;
    clone.style.fontStyle = cs.fontStyle;
    clone.style.letterSpacing = cs.letterSpacing;
    clone.style.textTransform = cs.textTransform;
    document.body.appendChild(clone);
    try {
      const sh = clone.scrollHeight;
      const lines = countLines(clone);
      return { sh, lines };
    } finally {
      clone.remove();
    }
  }

  function columnWidthPx(wrap) {
    const body = wrap.closest('.cart-upsell-offer__body');
    const el = body || wrap;
    const w = el.getBoundingClientRect().width;
    return Math.floor(w);
  }

  function fitCartUpsellLabel(labelEl) {
    const wrap = labelEl.closest(SEL_WRAP);
    if (!wrap || !labelEl.textContent.trim()) return;

    const csWrap = getComputedStyle(wrap);
    const maxFs = parseCssPx(csWrap.getPropertyValue('--cart-upsell-label-max-fs'), 36);
    const slotH = parseCssPx(csWrap.getPropertyValue('--cart-upsell-label-slot-h'), maxFs);
    const minFs = 10;
    const maxLines = 2;
    const slotSlack = 6;

    wrap.style.maxHeight = '';
    wrap.style.overflow = '';

    const colW = columnWidthPx(wrap);
    if (colW < 8) return;

    function ok(fs) {
      const { sh, lines } = measureClone(labelEl, colW, fs);
      return lines <= maxLines && sh <= slotH + slotSlack;
    }

    let best = minFs;
    if (ok(maxFs)) {
      best = maxFs;
    } else {
      let lo = minFs;
      let hi = maxFs;
      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        if (ok(mid)) {
          best = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
    }

    labelEl.style.fontSize = best + 'px';
    labelEl.style.lineHeight = best + 'px';

    const check = measureClone(labelEl, colW, best);
    if (check.lines > maxLines || check.sh > slotH + slotSlack) {
      labelEl.style.fontSize = minFs + 'px';
      labelEl.style.lineHeight = minFs + 'px';
      labelEl.classList.add('cart-upsell-offer__label--line-clamp');
    } else {
      labelEl.classList.remove('cart-upsell-offer__label--line-clamp');
    }

    // slotH targets “one line at max” for font picking; real glyphs (esp. iOS Safari) are often
    // taller than line-height:1, so a fixed max-height clips the last line — size the wrap to the
    // laid-out text + buffer instead.
    void labelEl.offsetHeight;
    const liveH = Math.ceil(
      Math.max(labelEl.scrollHeight, labelEl.getBoundingClientRect().height)
    );
    wrap.style.maxHeight = liveH + 12 + 'px';
    wrap.style.overflow = 'hidden';
  }

  function bindResize(wrap, label) {
    if (roMap.has(wrap)) return;
    const ro = new ResizeObserver(() => scheduleFit());
    ro.observe(wrap);
    const body = wrap.closest('.cart-upsell-offer__body');
    if (body) ro.observe(body);
    roMap.set(wrap, ro);
  }

  function fitAll() {
    document.querySelectorAll(SEL_WRAP).forEach((wrap) => {
      const label = wrap.querySelector(SEL_LABEL);
      if (!label) return;
      bindResize(wrap, label);
      fitCartUpsellLabel(label);
    });
  }

  function scheduleFit() {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      rafId = 0;
      fitAll();
    });
  }

  window.mfrCartUpsellLabelFit = { schedule: scheduleFit, fitAll: fitAll };

  function boot() {
    scheduleFit();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('cartReady', () => {
    requestAnimationFrame(() => scheduleFit());
  });

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => scheduleFit());
  }
})();
